import { defineStore } from 'pinia';
import config from '@/plugins/config';
import { useUserStore } from './user';
import { app } from '@/main.js';

export const defaultState = () => ({
  accessToken: null,
  refreshToken: null,
  authenticated: false,
  tokenExpiry: null,
  jwtPayload: null,
  isSuperAdmin: false,
  isOrganisationAdmin: false,
  isCoreAdmin: false,
  isGeneralUser: false,
  isTrainingAdmin: false,
  token: null,
  loginLoop: 0,
  isTokenExpires: false,
  selectedOrganisation: null,
  accessType: null,
  hasOrg: false,
  isManager: false,
  hasTraining: false,
  hasPentest: false,
  hasPhishing: false,
  hasECC: false,
  hasCoreProduct: false,
  orgProducts: null,
  isPhishingAdmin: false,
  isECCAdmin: false,
  isPentestAdmin: false,
  hasPolicy: false,
  isPolicyAdmin: false,
  isPentester: false,
});

export const useAuthStore = defineStore('auth', {
  state: defaultState,
  getters: {},
  actions: {
    resetState() {
      Object.assign(this, defaultState());
    },

    authenticated(authenticated) {
      this.authenticated = authenticated;
    },

    setAccessToken(accessToken) {
      this.accessToken = accessToken;
    },

    setToken(token) {
      this.token = token;
    },

    setTokenExpires() {
      this.isTokenExpires = true;
    },

    setLoginLoop(loginLoop) {
      this.loginLoop = loginLoop > 0 ? loginLoop : 0;
    },

    logout(payload = {}) {
      let errorMessage = '';
      let logoutAuth = true;

      if (typeof payload === 'string') {
        errorMessage = payload;
      } else if (typeof payload === 'object' && payload !== null) {
        if (payload.message || payload.errorMessage) {
          errorMessage = payload.message || payload.errorMessage;
        }
        if (typeof payload.logoutAuth === 'boolean') {
          logoutAuth = payload.logoutAuth;
        } else if (payload.isIdle || payload.idle) {
          logoutAuth = false;
        }
      }

      let baseLoginLink = '';
      try {
        baseLoginLink = app?.config?.globalProperties?.$config?.getLoginLink?.() || config.getLoginLink();
      } catch (e) {
        baseLoginLink = config.getLoginLink();
      }

      let logoutUrl = baseLoginLink;

      if (this.token) {
        logoutUrl += `&token=${encodeURIComponent(this.token)}`;
      }

      if (logoutAuth) {
        logoutUrl += '&logout=true';
      }

      if (this.loginLoop > 0) {
        logoutUrl += `&login_loop=${this.loginLoop}`;
      }

      if (this.isTokenExpires) {
        logoutUrl += '&error=TOKEN_EXPIRE';
      }

      if (errorMessage) {
        logoutUrl += `&message=${encodeURIComponent(errorMessage)}`;
      }

      const authApiBase = (process.env.VUE_APP_AUTH_API_BASE_URL || '').replace(/\/+$/, '');
      const currentToken = this.accessToken || this.token;
      const userId = this.jwtPayload?.user_id;

      this.resetState();
      try {
        const userStore = useUserStore();
        userStore.resetState();
      } catch (e) {
        console.warn('Could not reset user store during logout:', e);
      }
      localStorage.clear();
      sessionStorage.clear();

      if (authApiBase && currentToken && userId) {
        fetch(`${authApiBase}/users/${userId}/logout`, {
          method: 'POST',
          keepalive: true,
          headers: { Authorization: `Bearer ${currentToken}`, 'Content-Type': 'application/json' },
        })
          .catch(() => {})
          .finally(() => {
            window.location.replace(logoutUrl);
          });
      } else {
        window.location.replace(logoutUrl);
      }
    },

    setJwtPayload(payload) {
      this.jwtPayload = payload;
      this.tokenExpiry = payload?.expires_at;
      this.isManager = payload?.is_manager;

      if (payload && payload.roles) {
        this.isSuperAdmin = payload.roles.includes('super_admin');

        // for vertex roles
        if (!this.isSuperAdmin) {
          this.accessType = payload.roles.includes('vertex_analyst') ? 'vertex_analyst' : null;
          this.isPentester = payload.roles.includes('penetration_tester');
        }
      }

      this.hasCoreProduct = false;
      const coreProducts = [
        'automated_test_phishing_campaigns',
        'cyber_security_online_awareness_training',
        'email_compromise_check',
        'policy_management',
      ];

      const roles = Array.isArray(payload?.organisation_role) ? payload.organisation_role : [];
      if (roles.length > 0) {
        this.hasOrg = true;

        // Check across all organisations for any core product presence
        roles.forEach((org) => {
          if (Array.isArray(org.products)) {
            org.products.forEach((p) => {
              if (coreProducts.includes(p.product)) {
                this.hasCoreProduct = true;
              }
            });
          }
        });

        // Find primary Core organisation, or fallback to first organisation
        const primaryOrg = roles.find((org) =>
          Array.isArray(org.products) && org.products.some((p) => coreProducts.includes(p.product))
        ) || roles[0];

        if (primaryOrg && Array.isArray(primaryOrg.products)) {
          this.isOrganisationAdmin = Boolean(primaryOrg.is_organisation_admin);

          let isAnyAdmin = false;
          let isAnyGeneralUser = false;

          primaryOrg.products.forEach((p) => {
            if (
              [
                'automated_test_phishing_campaigns',
                'cyber_security_online_awareness_training',
                'pentester_platform',
                'email_compromise_check',
                'policy_management',
              ].includes(p.product)
            ) {
              this.hasTraining = p.product === 'cyber_security_online_awareness_training' ? true : this.hasTraining;
              this.hasPentest = p.product === 'pentester_platform' ? true : this.hasPentest;
              this.hasPhishing = p.product === 'automated_test_phishing_campaigns' ? true : this.hasPhishing;
              this.hasECC = p.product === 'email_compromise_check' ? true : this.hasECC;
              this.hasPolicy = p.product === 'policy_management' ? true : this.hasPolicy;

              if (p.roles && p.roles.includes('admin')) {
                isAnyAdmin = true;
              }
              if (p.roles && p.roles.includes('general_user')) {
                isAnyGeneralUser = true;
              }

              this.updateRoles({ product: p.product, roles: p.roles });
            }
          });

          // After looping through products
          this.isCoreAdmin = isAnyAdmin;
          this.isGeneralUser = !isAnyAdmin && isAnyGeneralUser;

          if (this.isOrganisationAdmin === true) {
            this.isPentestAdmin = this.hasPentest;
            this.isPhishingAdmin = this.hasPhishing;
            this.isECCAdmin = this.hasECC;
            this.isTrainingAdmin = this.hasTraining;
            this.isPolicyAdmin = this.hasPolicy;
          }
        }
      }

      // If no core product is present, log out the user
      if (!this.hasCoreProduct && !this.isSuperAdmin) {
        this.logout('User is not permissioned to access this product. Please contact your administrator.');
      }
    },

    updateRoles({ product, roles }) {
      if (product === 'cyber_security_online_awareness_training') {
        this.hasTraining = true;
      } else if (product === 'pentester_platform') {
        this.hasPentest = true;
      } else if (product === 'automated_test_phishing_campaigns') {
        this.hasPhishing = true;
      } else if (product === 'policy_management') {
        this.hasPolicy = true;
      }

      const isAdminForThisProduct = roles && roles.includes('admin');

      // Set admin flags
      if (isAdminForThisProduct) {
        this.isCoreAdmin = true;
        if (product === 'cyber_security_online_awareness_training') {
          this.isTrainingAdmin = true;
        }
        if (product === 'automated_test_phishing_campaigns') {
          this.isPhishingAdmin = true;
        }
        if (product === 'email_compromise_check') {
          this.isECCAdmin = true;
        }
        if (product === 'pentester_platform') {
          this.isPentestAdmin = true;
        }
        if (product === 'policy_management') {
          this.isPolicyAdmin = true;
        }
      }
    },

    setOrgProducts(products) {
      if (!Array.isArray(products)) return;
      const filteredProducts = products.filter((p) => p && p.organisation_product_id);
      this.orgProducts = filteredProducts;

      this.hasTraining = filteredProducts.some((p) => p.product && ['cyber_security_online_awareness_training', 'cyber security online awareness training'].includes(p.product.toLowerCase()));
      this.hasPentest = filteredProducts.some((p) => p.product && ['pentester_platform', 'pentester platform'].includes(p.product.toLowerCase()));
      this.hasPhishing = filteredProducts.some((p) => p.product && ['automated_test_phishing_campaigns', 'automated test phishing campaigns'].includes(p.product.toLowerCase()));
      this.hasECC = filteredProducts.some((p) => p.product && ['email_compromise_check', 'email compromise check'].includes(p.product.toLowerCase()));
      this.hasPolicy = filteredProducts.some((p) => p.product && ['policy_management', 'policy management'].includes(p.product.toLowerCase()));

      if (this.isOrganisationAdmin === true) {
        this.isPentestAdmin = this.hasPentest;
        this.isPhishingAdmin = this.hasPhishing;
        this.isECCAdmin = this.hasECC;
        this.isTrainingAdmin = this.hasTraining;
        this.isPolicyAdmin = this.hasPolicy;
      }
    },
  },
  persist: true,
});

export default useAuthStore;